import { toastApp } from '@/components/toast-message/sonner';
import { useCheckClosePair } from '@/hooks/useCheckClosePair';
import { useAddress } from '@/store/selector';
import { formatDisplay } from '@/utils/format-display';
import clsx from 'clsx';
import { ethers } from 'ethers';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useBalance } from '../data';
import { buysell } from '../func';
import { useValidate } from '../hooks/useValidate';
import { FormValue, Type } from '../type';
import { useGetListQuickTrade } from './data';

export function QuickTrade() {
    const { control, setValue, handleSubmit } = useFormContext<FormValue>();

    const navigate = useNavigate();

    const address = useAddress();

    const { validateForm } = useValidate();

    const { t } = useTranslation();

    const { data: balance } = useBalance();

    const [type, pairIndex] = useWatch({
        control,
        name: ['type', 'pairIndex'],
    });

    const { listQuickTrade } = useGetListQuickTrade();
    const { isClose } = useCheckClosePair(Number(pairIndex));

    return (
        <div className="grid grid-cols-2 gap-2">
            {listQuickTrade?.map((item, index) => (
                <button
                    disabled={
                        Number(ethers.formatUnits(balance ?? 0)) < item.value ||
                        isClose
                    }
                    key={index}
                    data-action={`submit-${type}`}
                    data-category="long-short"
                    data-label="quick-action"
                    className={clsx('border border-white/10 pt-3 rounded-xl', {
                        'opacity-50':
                            Number(ethers.formatUnits(balance ?? 0)) <
                                item.value || isClose,
                    })}
                    onClick={async () => {
                        setValue('amount', item.value.toString());
                        setValue('leverage', item.leverage);

                        const errors = validateForm(
                            type === Type.Long ? Type.Long : Type.Short,
                            false
                        );

                        if (errors) {
                            return toastApp.error(errors);
                        }

                        handleSubmit(
                            buysell({
                                address,
                                buy: type === Type.Long,
                                onSuccess: () => {
                                    navigate('/orders', {
                                        state: { isOpened: true },
                                    });
                                },
                            })
                        )();
                    }}
                >
                    <div className="flex items-center justify-between px-3 mb-[6px]">
                        <p className="text-base font-semibold text-white">
                            {item.value} WLD
                        </p>

                        <div className="flex items-center justify-center h-5 px-[6px] rounded-[6px] bg-white">
                            <p className="text-xs font-medium">
                                x{item.leverage}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-white/10 bg-[#141414)]">
                        <div className="flex flex-col gap-1">
                            <p className="text-[10px] leading-3 text-[#808080]">
                                {t('Position')}
                            </p>
                            <p className="text-xs text-[#808080]">
                                {item.value * item.leverage} WLD
                            </p>
                        </div>

                        <div className="flex flex-col gap-1">
                            <p className="text-[10px] leading-3 text-[#808080]">
                                {t('To Earn')}
                            </p>
                            <p className="text-xs text-[#38F24A]">
                                {formatDisplay(item.value * 9, {
                                    decimalToShow: 2,
                                })}{' '}
                                WLD
                            </p>
                        </div>
                    </div>
                </button>
            ))}
        </div>
    );
}
